import { type EntitySource, parentsOf, transformSource, translation } from '@/domain/construction';
import { type Vec2 } from '@/domain/math';
import { type Entity, type EntityId, type SceneDocument } from '@/domain/model';

import { type EditAction } from './actions';

/**
 * Copying and pasting a construction.
 *
 * ## Why this is not Figma's copy, and is n8n's
 *
 * In Figma an object is self-contained, so copying it is copying it. Here the document is a
 * **directed graph**: the midpoint of `AB` has no coordinates of its own, and a copy of it that
 * did not bring `A` and `B` along would not be a midpoint of anything. It would not merely look
 * wrong — it would fail to evaluate.
 *
 * n8n has the same shape of problem, because its nodes have connections, and its answer is the
 * right one to borrow: copy the **subgraph**, not the nodes. The only adjustment is which way
 * the closure runs. n8n can drop a connection into a node that is not coming along, because a
 * node still runs without input. A construction cannot: what it is built on *is* what it is. So
 * the closure goes **upwards**, over ancestors.
 *
 * That is the same rule {@link movableRootsOf} uses for dragging a selection, and deliberately
 * the same — a user who has learned that selecting three segments moves the triangle should not
 * have to learn a second, different rule for what copying them gives.
 *
 * ## What it does not close over
 *
 * Descendants. Copying a point does not bring the circle drawn round it, or the label anchored
 * to it. Those were not selected, and a copy that quietly grew every consequence of what you
 * pointed at would be impossible to predict. In practice a marquee round a figure catches its
 * labels anyway, because they are inside the box.
 */

/** A construction lifted out of the document, and how many times it has been put back. */
export interface Clipboard {
  readonly entities: readonly Entity[];
  /**
   * Pastes made from this clipboard so far.
   *
   * Each lands one step further from the last, so that pasting three times gives three copies
   * a user can see and take hold of, rather than three in a stack that looks like one.
   */
  readonly pastes: number;
}

/** How far each paste lands from the one before, in world units. */
export const PASTE_OFFSET = 24;

/**
 * Everything the selection needs in order to mean the same thing somewhere else.
 *
 * Empty when the selection is, and empty when nothing in it exists — the caller can treat an
 * empty result as "there is nothing to copy" without a second question.
 */
export function copyClosure(
  document: SceneDocument,
  selection: Iterable<EntityId>
): readonly Entity[] {
  const wanted = new Set<EntityId>();
  const queue = [...selection];

  while (queue.length > 0) {
    const current = queue.pop();
    if (current === undefined) break;
    if (wanted.has(current)) continue;

    const entity = document.entities.get(current);
    if (entity === undefined) continue;

    wanted.add(current);
    queue.push(...parentsOf(entity.source));
  }

  /*
   * In the document's own order, not the order the walk happened to reach them in. A paste
   * writes `add-entity` actions in sequence, and a child added before its parent would be a
   * dangling reference for as long as it took the next action to land.
   */
  return [...document.entities.values()].filter((entity) => wanted.has(entity.id));
}

/**
 * The edits that put a copied construction back, under fresh ids and clear of the original.
 *
 * `mint` supplies the new ids, because uniqueness is the caller's business: it holds the
 * generator that the rest of the session draws from, and a clipboard that made up its own names
 * could collide with something added since the copy.
 */
export function pasteActions(
  document: SceneDocument,
  entities: readonly Entity[],
  mint: () => EntityId,
  offset: Vec2
): { readonly actions: readonly EditAction[]; readonly created: readonly EntityId[] } {
  if (entities.length === 0) return { actions: [], created: [] };

  const remap = new Map<EntityId, EntityId>(entities.map((entity) => [entity.id, mint()]));

  /*
   * On top, keeping their order relative to each other. Reusing the originals' own `zIndex`
   * would interleave the copy with the drawing it was taken from, which reads as one figure
   * rather than two.
   */
  const base = document.entities.size;
  const ranked = [...entities].sort((a, b) => a.zIndex - b.zIndex);

  const actions: EditAction[] = [];
  const created: EntityId[] = [];

  for (const [rank, entity] of ranked.entries()) {
    const id = remap.get(entity.id);
    if (id === undefined) continue;
    created.push(id);

    /*
     * The offset is applied to the sources that carry coordinates and to no others. A derived
     * entity returns `null` here, and rightly: moving a midpoint is something you do by moving
     * the segment, and writing a position into it would detach it from its own definition.
     */
    const moved = transformSource(entity.source, translation(offset)) ?? entity.source;

    actions.push({
      kind: 'add-entity',
      entity: { ...entity, id, source: remapSource(moved, remap), zIndex: base + rank },
    });
  }

  return { actions, created };
}

/**
 * Rewrites the entity references inside a source.
 *
 * Walks the fields generically rather than switching on `op`: a source is a flat record of ids,
 * numbers and small objects, and a switch here would be a second place that has to learn about
 * every new construction. The ids are branded strings, so anything present in the map is a
 * reference and anything absent is a value.
 */
export function remapSource(
  source: EntitySource,
  remap: ReadonlyMap<EntityId, EntityId>
): EntitySource {
  const out: Record<string, unknown> = {};

  const rename = (value: unknown): unknown =>
    typeof value === 'string' ? (remap.get(value as EntityId) ?? value) : value;

  for (const [key, value] of Object.entries(source) as [string, unknown][]) {
    out[key] = Array.isArray(value) ? (value as unknown[]).map(rename) : rename(value);
  }

  return out as unknown as EntitySource;
}
